import type { AccountMergingResponse } from "../../application/services/accountMergingService";
import { Loader2 } from "lucide-react";
import { formatAccountStats } from "../hooks/useAccountMerging";
import { useIsMobile } from "@/core/hooks/useMediaQuery";

interface Props {
  data: AccountMergingResponse;
  isLoading: boolean;
  primaryEmail: string | null;
  onSelect: (email: string) => void;
  onContinue: () => void;
  getAvatarColor: (index: number) => string;
  handleLogout: () => void;
}

export function AccountMergingPrimaryScreen({
  data,
  isLoading,
  primaryEmail,
  onSelect,
  onContinue,
  getAvatarColor,
  handleLogout,
}: Props) {

  const isMobile = useIsMobile();

  return (
    <>
      <div className="items-center mb-[10px]">
        <p className={`font-semibold ${isMobile ? 'text-lg mb-2' : 'text-xl mb-[10px]'}`}>
          Multiple emails linked to your number {data.customerPhone}
        </p>

        <p className={`text-gray-600 font-semibold ${isMobile ? 'text-sm' : 'text-md'}`}>
          To improve sign-in, we're moving to mobile-number based authentication.
          Please <strong className="text-black">choose your primary account</strong> to continue
        </p>

        {!isMobile && <div className="flex items-center gap-3 border border-[#438AE780] bg-[#EDF4FD66] p-3 mt-3">
          <div className="flex items-center justify-center w-6 h-6 shrink-0 rounded-full border-2 border-[#438AE7] text-[#438AE7] text-sm font-semibold">
            i
          </div>
          <p className="text-black text-md">
            Your orders and benefits won't be lost. You are in control of what gets merged or moved.
          </p>
        </div>}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {isLoading && (
          <div className="flex justify-center gap-3 text-sm text-gray-500">
            <Loader2 className="animate-spin" /> Loading accounts
          </div>
        )}

        {!isLoading && (
          <div
            className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto py-3
            [mask-image:linear-gradient(to_bottom,transparent_0px,black_20px,black_calc(100%-20px),transparent_100%)]
            [-webkit-mask-image:linear-gradient(to_bottom,transparent_0px,black_20px,black_calc(100%-20px),transparent_100%)]"
          >
            {Object.entries(data.customerAccountsData).map(
              ([email, details], index) => {
                const isSelected = primaryEmail === email;

                const stats = formatAccountStats(details);

                return (
                  <div key={email} onClick={() => onSelect(email)}
                    className={`border flex flex-col cursor-pointer transition
                    ${isSelected ? "border-[#F15D47] bg-[#FFFBFA]" : "border-gray-200 bg-white"}`}
                  >
                    <div className={`flex items-center border-b border-gray-200 ${isMobile ? "py-[12px] px-[15px]":"px-[20px] py-[15px] "}`}>
                      <div
                        className={`rounded-full flex items-center justify-center text-white mr-4 flex-shrink-0 ${isMobile ? 'w-9 h-9 text-lg' : 'w-10 h-10 text-xl'}`}
                        style={{ backgroundColor: getAvatarColor(index) }}
                      >
                        {email.charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className={`text-[#1a1a4b] font-medium truncate ${isMobile ? 'text-sm' : 'text-base'}`}>
                          {email}
                        </div>
                      </div>

                      <div className="ml-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition
                          ${isSelected
                            ? "border-[#F15D47] bg-[#F15D47]"
                            : "border-gray-300 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                      </div>

                    </div>

                    <div className={`flex items-stretch
                        ${isMobile ? "flex-col divide-y divide-gray-200" : "py-[15px] px-[10px] flex-row divide-x divide-gray-200"}
                      `}
                    >
                      {stats.map((stat) => (
                        <StatItem
                          key={stat.label}
                          value={stat.value}
                          label={stat.label}
                          isMobile={isMobile}
                        />
                      ))}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
      <div className={`flex ${isMobile ? "flex-col flex-col-reverse" : "gap-2"}`}>
        <button onClick={handleLogout} className={`p-[10px] text-md disabled:opacity-50 ${!isMobile ? "border" : "pb-0 text-[#4a90e2]"}`}>
          Logout
        </button>
        <button
          disabled={!primaryEmail}
          onClick={onContinue}
          className="w-full p-[14px] text-md bg-hover text-white disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </>
  );
}



interface StatItemProps {
  value: React.ReactNode;
  label: string;
  isMobile: boolean;
}

function StatItem({ value, label, isMobile }: StatItemProps) {
  return (
    <div className={`flex flex-auto min-w-0
        ${isMobile ? "flex-row items-center justify-between py-[10px] px-[15px]" : "flex-col px-[10px]"}
      `}
    >
      {isMobile ? (
        <>
          <span className="text-gray-500 font-semibold text-xs">{label}</span>
          <span className="text-[#1a1a4b] font-semibold text-sm">
            {value}
          </span>
        </>
      ) : (
        <>
          <span className="text-[#1a1a4b] font-semibold text-md mb-1 leading-[1.2]">
            {value}
          </span>
          <span className="text-gray-500 text-[11px]">{label}</span>
        </>
      )}
    </div>
  );
}
